import { useEffect, useMemo, useState } from "react";
import { lettersApi, ApiError } from "@/api";
import type { LetterListRow, LetterSource } from "@/types";
import { Button, Spinner, Pill } from "@/components/ui";
import { humanise, letterConfidenceTone, letterSourceTone, letterSourceLabel } from "@/lib/jobs";
import { formatDateShort } from "@/lib/format";
import { useAuth } from "@/staff/auth/AuthContext";
import { LetterDetailDrawer } from "@/staff/jobs/LetterDetailDrawer";
import { useUnread } from "./UnreadContext";
import "./letters-inbox.css";

const PER_PAGE = 25;
const READ_GRACE_DAYS = 14; // "Recent" keeps letters read in the last N days

type View = "recent" | "unread" | "all";
type SourceFilter = "" | LetterSource;

const SOURCES: { value: SourceFilter; label: string }[] = [
  { value: "", label: "All sources" },
  { value: "planning", label: "Planning" },
  { value: "building_control", label: "Building control" },
];

/** Letters inbox — unified planning + BC letters with an unread/all toggle,
 *  source filter, search, and a read-on-open detail drawer. Read-receipts use
 *  the authed user's Xano id (auth/me `id`). */
export function LettersInbox() {
  const { user } = useAuth();
  const userId = user?.id;
  const { unread, refresh: refreshUnread } = useUnread();

  const [items, setItems] = useState<LetterListRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0); // bump to retry

  const [view, setView] = useState<View>("recent");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState<{ id: number; source: LetterSource; isRead: boolean } | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  // Debounced search (resets to page 1, batched so the fetch fires once).
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    lettersApi
      .listLetters({
        user_id: userId,
        // Recent + Unread both filter to unread server-side; Recent also keeps
        // the last 14 days of read letters (the endpoint requires
        // include_read_days >= 1, so Unread sends 1 and we drop read rows below).
        unread_only: view !== "all",
        include_read_days: view === "recent" ? READ_GRACE_DAYS : 1,
        source: sourceFilter || undefined,
        q: q || undefined,
        page,
        per_page: PER_PAGE,
        sort_field: "letter_date",
        sort_direction: "desc",
      })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items ?? []);
        setTotalPages(Math.max(1, res.paging?.total_pages ?? 1));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load letters.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, view, sourceFilter, q, page, reloadKey]);

  /** Reflect a read/unread change from the detail drawer in the row in place
   *  (no re-sort) and refresh the badge. */
  function applyReadChange(id: number, source: LetterSource, read: boolean) {
    setItems((prev) => prev.map((r) => (r.id === id && r.source === source ? { ...r, is_read: read } : r)));
    void refreshUnread();
  }

  /** The unread badge count for the source currently filtered to — the number
   *  `mark-all-read` would clear, since that endpoint honours `source` and
   *  nothing else. */
  const unreadInScope =
    sourceFilter === "planning"
      ? unread?.planning
      : sourceFilter === "building_control"
        ? unread?.building_control
        : unread?.total;

  const scopeLabel =
    sourceFilter === "planning"
      ? "planning letters"
      : sourceFilter === "building_control"
        ? "building control letters"
        : "letters";

  /** Bulk read-receipt for everything unread in the current source. Deliberately
   *  ignores the search box (the endpoint takes no `q`), so the confirm says so
   *  when one is active. */
  async function markAllRead() {
    if (!userId || markingAll) return;
    const n = unreadInScope;
    const confirmMessage =
      `Mark ${n ? `all ${n}` : "every"} unread ${scopeLabel} as read?` +
      (q ? ` This covers every unread ${scopeLabel}, not just the ones matching “${q}”.` : "");
    if (!window.confirm(confirmMessage)) return;
    setMarkingAll(true);
    setError(undefined);
    try {
      await lettersApi.markAllRead(userId, sourceFilter || undefined);
      void refreshUnread();
      setPage(1);
      setReloadKey((k) => k + 1); // batched with setPage — one refetch
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not mark the letters as read.");
    } finally {
      setMarkingAll(false);
    }
  }

  // "Unread" returns only-unread: the endpoint's mandatory include_read_days=1
  // can still admit letters read today, so drop any read rows client-side.
  const displayed = useMemo(
    () => (view === "unread" ? items.filter((r) => !r.is_read) : items),
    [items, view],
  );

  const emptyMessage = useMemo(
    () =>
      view === "unread"
        ? "You're all caught up — no unread letters."
        : view === "recent"
          ? "Nothing recent."
          : "No letters found — try clearing filters.",
    [view],
  );

  return (
    <section className="ltr">
      <header className="ltr__head">
        <h1>Letters</h1>
      </header>

      <div className="ltr__controls">
        <div className="ltr__seg" role="tablist" aria-label="Read filter">
          {([
            ["recent", "Recent"],
            ["unread", "Unread"],
            ["all", "All"],
          ] as [View, string][]).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              className={`ltr__seg-btn${view === v ? " is-on" : ""}`}
              onClick={() => { setView(v); setPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="ltr__seg" role="tablist" aria-label="Source filter">
          {SOURCES.map((s) => (
            <button
              key={s.value || "all"}
              type="button"
              role="tab"
              aria-selected={sourceFilter === s.value}
              className={`ltr__seg-btn${sourceFilter === s.value ? " is-on" : ""}`}
              onClick={() => { setSourceFilter(s.value); setPage(1); }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <input
          className="leads__search"
          type="search"
          placeholder="Search letters…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
        />

        {view === "unread" ? (
          <Button
            className="ltr__mark-all"
            variant="secondary"
            size="sm"
            loading={markingAll}
            disabled={!userId || unreadInScope === 0}
            onClick={() => void markAllRead()}
          >
            Mark all as read
          </Button>
        ) : null}
      </div>

      {error ? (
        <p className="leads__error" role="alert">
          {error}{" "}
          <button type="button" className="ltr__retry" onClick={() => setReloadKey((k) => k + 1)}>Retry</button>
        </p>
      ) : null}

      {loading ? (
        <div className="jobs-list__loading"><Spinner label="Loading letters…" /></div>
      ) : displayed.length === 0 ? (
        <p className="jobs-list__empty">{emptyMessage}</p>
      ) : (
        <div className="ltr__table-wrap">
          <table className="ltr__table">
            <thead>
              <tr>
                <th aria-label="Unread" />
                <th>Source</th>
                <th>Type</th>
                <th>Date</th>
                <th>Confidence</th>
                <th>Application</th>
                <th>Job</th>
                <th aria-label="Task" />
              </tr>
            </thead>
            <tbody>
              {displayed.map((row) => (
                <tr
                  key={`${row.source}-${row.id}`}
                  className={`ltr-row${row.is_read ? " ltr-row--read" : ""}`}
                  onClick={() => setOpen({ id: row.id, source: row.source, isRead: row.is_read })}
                  title="Open letter"
                >
                  <td className="ltr-row__dot">{!row.is_read ? <span className="ltr-dot" aria-label="Unread" /> : null}</td>
                  <td><Pill tone={letterSourceTone(row.source)}>{letterSourceLabel(row.source)}</Pill></td>
                  <td>
                    <span className="ltr-row__type">{humanise(row.letter_type)}</span>
                    {row.letter_subtype ? <span className="ltr-row__subtype">{humanise(row.letter_subtype)}</span> : null}
                  </td>
                  <td className="ltr-row__date">{formatDateShort(row.letter_date)}</td>
                  <td>{row.confidence ? <Pill tone={letterConfidenceTone(row.confidence)}>{humanise(row.confidence)}</Pill> : <span className="ltr-row__muted">—</span>}</td>
                  <td>
                    {row.application_reference ? (
                      <>
                        <span className="ltr-row__appref numeric">{row.application_reference}</span>
                        {row.application_type ? <span className="ltr-row__subtype">{humanise(row.application_type)}</span> : null}
                      </>
                    ) : (
                      <span className="ltr-row__muted">—</span>
                    )}
                  </td>
                  <td className="ltr-row__job">{row.job_name || "—"}</td>
                  <td>{row.has_linked_task ? <span className="ltr-row__task" title={row.task_title ?? "Linked task"}>✓ task</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && displayed.length > 0 && totalPages > 1 ? (
        <div className="jobs-pager">
          <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Previous</Button>
          <span className="jobs-pager__status">Page {page} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next →</Button>
        </div>
      ) : null}

      {open ? (
        <LetterDetailDrawer
          id={open.id}
          source={open.source}
          userId={userId}
          isRead={open.isRead}
          onReadChange={(read) => applyReadChange(open.id, open.source, read)}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </section>
  );
}
